<template>
  <v-app>
    <!-- Uma cor só, em toda tela (pedido do usuário). Já houve uma por modo — uso, design e acesso
         —, com um selo embaixo do logo; os modos saíram, e o design de cada coisa é um degrau do
         caminho. Ver `utils/destinations`. -->
    <!-- Nas rotas PÚBLICAS a casca não é desenhada: entrar e se cadastrar têm a tela inteira, e
         o painel de marca delas já traz o logo. Com a barra aqui, seriam dois logos na mesma
         tela — e a porta de entrada continuaria parecendo mais uma página de dentro. -->
    <v-app-bar v-if="!$route.meta.public" :color="barColor" theme="dark"
               density="compact" :order="0">
      <!-- Sem link: a casinha à direita é o caminho para a home, e dois caminhos para o
           mesmo lugar na mesma barra só fazem o usuário se perguntar se são diferentes. A folga
           cresce com a largura: colada no telefone, larga no desktop. -->
      <div class="d-flex flex-column justify-center me-2 me-sm-4 me-md-8">
        <h1 class="app-bar-logo text-teal-accent-3"><span class="text-white">lunedi</span></h1>
      </div>

      <!-- O ambiente e as coleções ocupam a barra. Antes havia aqui um título
           centralizado por um sanduíche de calços — um v-spacer condicional, um mx-auto e
           outro v-spacer — e um menu de troca de ambiente comentado, que este seletor
           finalmente torna real. As abas crescem para ocupar a sobra, o que dispensa o
           segundo calço. -->
      <template v-if="hasWorkspace">
        <ontos-workspace-menu :workspace="workspace"
                              @reload="onUpdateSystem()"
                              @showErrorMessage="showErrorMessage($event)"/>
        <ontos-collection-tabs :workspace="workspace"/>
      </template>
      <v-spacer v-else/>
      <!-- `activator="parent"` em vez do slot de ativador: o mesmo tooltip em quatro
           linhas a menos por botão, sem intermediário no template.

           O grupo inteiro é de quem está dentro: deslogado não há ambiente para encerrar, modo
           para trocar, casa para onde voltar nem conta de onde sair — e um ícone que leva a uma
           rota protegida é promessa que não se cumpre. Uma condição para o grupo, e não uma em
           cada botão, porque a resposta é a mesma para todos. -->
      <div v-if="$storage.user" class="d-flex align-center ga-1">
        <!-- As três ações que servem a qualquer tela do ambiente, nesta ordem (pedido do usuário):
             criar (a página de criar de cada coleção), buscar, e configurar (o design do ambiente e o
             de cada coleção, só para quem desenha). Na faixa do caminho criar e configurar abrem
             janela, no contexto da coleção; aqui, rota.

             O Configurar é o ÚLTIMO dos três porque é ele que sai quando falta lugar: no telefone ele
             vai para dentro do ⋮ (pedido do usuário), e o que sai é o da ponta.

             Dentro do grupo de quem está dentro, porque sem conta não há ambiente; e só com ambiente
             em mão, que é o que o `hasWorkspace` diz. -->
        <template v-if="hasWorkspace">
          <ontos-bar-menu icon="mdi-plus" :label="$tt('new')" :items="createItems"/>
          <ontos-global-search :workspace="workspace" @showErrorMessage="showErrorMessage($event)"/>
          <ontos-bar-menu v-if="!$vuetify.display.xs" icon="mdi-tune" :label="$tt('design-menu')"
                          :items="designItems"/>
        </template>
        <!-- O selo do encerrado, no grupo das AÇÕES e não junto do nome: ele é o único aviso
             de que o ambiente está congelado, e também a saída. Aqui ele fica na altura dos
             olhos de quem procura o que fazer, em vez de virar enfeite do título.

             Clicável, e reabre: encerrar mora no design, porque é decisão que se toma uma vez
             e de dentro do ambiente — mas REABRIR é o que se quer no instante em que se topa
             com a trava, e esperar que o usuário vá até o design para isso seria fazê-lo
             procurar a saída.

             Some sozinho ao reabrir, porque sai do mesmo workspace. E não há aviso nos botões
             porque não há botões: num ambiente encerrado eles não são desenhados.

             O selo aparece para TODOS, e só o administrador clica: reabrir é dele. Para os
             outros ele volta a ser o que já era antes de ser saída — o aviso de que está
             congelado —, e sem ponteiro de mão nem promessa que não se pode cumprir. -->
        <!-- No xs fica só o ícone, com o nome no tooltip: a palavra são ~108px, e ela sozinha
             pedia um terço da largura de um telefone em pé. A caixa SEM seta é o estado; as
             setas da mesma família (`mdi-archive-arrow-down/up`) são as ações de encerrar e
             reabrir, e misturá-las aqui diria duas coisas no mesmo pixel.

             O `aria-label` não é zelo: sem a palavra o selo fica sem NOME, e tooltip é descrição.
             E o tooltip passa a existir para quem não administra, mas só no xs — do sm para cima
             a palavra está na tela, e repeti-la seria ruído. -->
        <v-chip v-if="hasWorkspace && workspace.closed" size="small" variant="flat"
                class="closed-badge" :class="badgeClasses"
                :aria-label="$tt('closed')"
                @click="canReopen && (askingReopen = true)">
          <v-icon v-if="$vuetify.display.xs" size="16">mdi-archive-outline</v-icon>
          <template v-else>{{ $tt('closed') }}</template>
          <v-tooltip v-if="canReopen || $vuetify.display.xs" activator="parent" location="bottom">
            {{ canReopen ? $tt('workspace-reopen') : $tt('closed') }}
          </v-tooltip>
        </v-chip>

        <!-- A chave-mestra (era o "Ver tudo"): as fórmulas de acesso deixam de valer para o
             administrador (ver o `offersSeeAll`). Ligada, ela é o selo cheio, como o de encerrado —
             quem a usa precisa lembrar disso, porque está vendo o que os outros não veem.

             Chave ligada e chave desligada, o `key` e o `key_off` do Material Symbols (escolha do
             usuário) — ver o `MASTER_KEY_ICON`. Num `<svg>` próprio, e não no `v-icon`: o MDI não
             tem a chave desligada, e o SVG que o `v-icon` desenha tem o `viewBox` de 24 fixo, que
             cortaria o desenho do Material Symbols, feito em 960. -->
        <v-chip v-if="offersSeeAll" size="small" :variant="seeAll ? 'flat' : 'outlined'"
                class="see-all-chip" :aria-label="$tt('see-all.label')" @click="toggleSeeAll()">
          <svg class="master-key-icon" :class="{'master-key-icon--start': !$vuetify.display.xs}"
               viewBox="0 -960 960 960" aria-hidden="true">
            <path :d="seeAll ? MASTER_KEY_ICON.on : MASTER_KEY_ICON.off"/>
          </svg>
          <template v-if="!$vuetify.display.xs">{{ $tt('see-all.label') }}</template>
          <v-tooltip activator="parent" location="bottom">
            {{ seeAll ? $tt('see-all.on') : $tt('see-all.off') }}
          </v-tooltip>
        </v-chip>

        <!-- Para onde se pode ir daqui: o início do ambiente e a lista de ambientes. A engrenagem do
             design e o botão dos acessos saíram (pedido do usuário). Uma lista só, que no telefone
             vira um menu: ver o `NavDestinations`.

             O ambiente vai anulável: fora de um ambiente o `workspace` daqui continua sendo o do
             ANTERIOR, e quem sabe disso é esta casca. -->
        <ontos-nav-destinations :workspace="hasWorkspace ? workspace : null" :menus="foldedMenus"/>

        <!-- O `?.` não é excesso de zelo, e o v-if acima não basta: o conteúdo de um
             SLOT é uma função, executada não aqui e sim quando o componente filho — o
             v-avatar, o v-list-subheader — renderiza, o que acontece depois, na mesma
             rodada. Ao sair, o logout limpa o armazenamento entre uma coisa e outra: o
             v-if já viu usuário, a função de slot roda em seguida e não vê mais. Era
             assim que clicar em "Sair" estourava com "reading 'name'".

             O `$storage.user` É reativo — e só ele, no armazenamento inteiro. Sem isso, a
             casca não se redesenhava quando o valor mudava: ao sair sobrava uma bolinha vazia,
             porque o `v-if` já tinha dito "sim" e o nome já não existia; ao entrar, a bolinha
             só aparecia depois de recarregar a página. O porquê está no `storage.ts`.

             Isso não dispensa os `?.`: o redesenho acontece no tique seguinte, e a função de
             slot ainda roda neste. -->
        <v-menu>
          <template #activator="{ props }">
            <ontos-user-avatar v-bind="props" class="cursor-pointer"
                               :user="$storage.user?.user"
                               :label="$storage.user?.name || $storage.user?.email"
                               :photo="$storage.user?.photo"/>
          </template>
          <v-list nav>
            <!-- O nome quando há, o e-mail quando não. O servidor parou de inventar nome — ele
                 respondia `name = email` em memória, e o avatar de todo mundo mostrava a inicial
                 do endereço. Quem desenha quem não tem nome é a tela. -->
            <v-list-subheader>{{ $storage.user?.name || $storage.user?.email }}</v-list-subheader>
            <!-- O nível de quem olha NESTE ambiente, na cor dele (escolha do usuário): era a cor da
                 barra, quando cada modo vestia a do nível de quem trabalhava nele. O mesmo chip da
                 lista de acessos. -->
            <div v-if="hasWorkspace" class="px-4 pb-2">
              <ontos-colored-value :option="levelOption(workspace.accessLevel)" form="chip"/>
            </div>
            <v-list-item link :to="{name: 'profile'}">{{ $tt('profile') }}</v-list-item>
            <!-- A administração do lunedi mora AQUI, e não na home: este é o único menu que é da
                 pessoa, e convite é assunto de conta, não de ambiente. Pela home, quem estivesse
                 trabalhando teria de sair do ambiente para chegar lá.

                 Quem não aprova não vê: item que não leva a lugar nenhum é ruído, o mesmo
                 argumento do "mostrar encerrados". Quem garante é o portão do servidor.

                 Lido direto do storage, como o nome logo acima: ele é reativo, então o item
                 aparece e some sozinho quando o nível da conta muda. -->
            <v-list-item v-if="isApprover($storage.user)" link :to="{name: 'invitations'}">
              {{ $tt('invitations') }}
            </v-list-item>
            <!-- Só o @click: o :to que havia aqui levava a view-system ao mesmo tempo que
                 o logout empurrava para o login, e na home montava rota sem o parâmetro
                 obrigatório. -->
            <v-list-item link @click="logout()">{{ $tt('logout') }}</v-list-item>
          </v-list>
        </v-menu>
      </div>
    </v-app-bar>

    <!-- A faixa do caminho é uma barra IRMÃ, e não a extensão da primeira nem um bloco
         dentro do v-main. Na extensão ela herdaria o theme="dark" da barra colorida e
         teria altura fixa de 40px menos o padding que a compat acrescenta — não cabe um
         degrau de duas linhas. Dentro do v-main ela rolaria junto com a página, e o
         caminho é endereço: tem de ficar à vista.

         O `order` das duas é declarado de propósito. Elas montam e desmontam a cada
         troca de ambiente, porque o reloadSystem derruba e levanta o `ready`; sem ele a
         pilha dependeria da ordem de registro no layout. -->
    <!-- Some no xs, onde as abas de coleção já viraram seletor: ali o caminho não
         acrescenta nada. Os dois primeiros degraus são exatamente os dois seletores da
         barra, e o terceiro — o nome do item — é o título da própria tela. Repetir custa
         44px de altura justamente onde a tela é mais curta. -->
    <v-app-bar v-if="hasWorkspace && !$vuetify.display.xs"
               :order="1" :height="44" color="surface" flat border="b">
      <ontos-app-breadcrumb class="px-2" :workspace="workspace" :instance-label="instanceLabel"/>
      <v-spacer/>
      <!-- As visões da coleção moram AQUI quando esta faixa existe: é a faixa do lugar
           onde se está, e lista ou Kanban é justamente isso. Some do cabeçalho da
           página, que fica só com o título e as ações.
           No xs esta faixa não existe, e aí elas voltam para o cabeçalho — a condição
           espelhada está no ListInstances e no Kanban. -->
      <!-- Criar na coleção em que se está — na lista, no Kanban e na ficha de um item dela. Mora
           aqui, e não no cabeçalho da lista, pelo mesmo motivo das visões ao lado: é a faixa do
           lugar onde se está. No xs, sem esta faixa, ele volta para o cabeçalho da lista. -->
      <!-- A coleção do item aberto, pelo nome no plural, para voltar à lista ou ao Kanban dela — como
           a aba da barra, mas só a de agora (pedido do usuário). À esquerda dos outros dois: é para
           onde se volta, e eles são o que se faz. -->
      <ontos-header-action v-if="itemIdea" icon="mdi-table" class="me-2" :label="itemIdea.pluralName"
                           :to="itemCollectionRoute"/>
      <ontos-header-action v-if="createIdea" icon="mdi-plus" color="primary" class="me-2"
                           :label="$tt('instance-create', {collection: createIdea.name})"
                           @click="requestCreation(createIdea.alias)"/>
      <!-- O design da coleção em que se está, numa janela por cima — o que antes era um botão no
           cabeçalho de cada tela (pedido do usuário). Só para quem desenha. O rótulo é o mesmo dos
           itens do menu da barra: "Configurar Canções", no plural — configura-se a coleção, e não
           um item (pedido do usuário). -->
      <!-- O design do AMBIENTE, no início dele (pedido do usuário): a página, e não uma janela — o
           design do ambiente não tem janela, e é a tela inteira dele. Mesmo rótulo do item do menu
           "Configurar" da barra. Só para quem desenha, como o da coleção. -->
      <ontos-header-action v-if="designsWorkspaceHere" icon="mdi-tune" class="me-2"
                           :label="$tt('design-of', {name: workspace.title})"
                           :to="{name: 'design-system', params: {system: system.alias}}"/>
      <!-- E a volta, no design do ambiente: o nome dele, para o início — como o botão da coleção, no
           design dela, leva à lista (escolha do usuário). A casinha é a do `WORKSPACE_ICON`, a mesma
           da barra para esse destino. -->
      <ontos-header-action v-if="workspaceDesignOnStrip" :icon="WORKSPACE_ICON" class="me-2"
                           :label="workspace.title" :to="{name: 'view-system', params: {system: system.alias}}"/>
      <ontos-header-action v-if="designIdea" icon="mdi-tune" class="me-2"
                           :label="$tt('design-of', {name: designIdea.pluralName})"
                           @click="designing = designIdea.alias"/>
      <ontos-collection-view-tabs v-if="viewTabsIdea" :idea="viewTabsIdea"
                                  :ideas="workspace.ideas" class="me-2"/>
    </v-app-bar>

    <!-- A folga lateral que as duas gavetas vazias davam, agora dita em voz alta. Só do
         lg para cima porque era só aí que existia: as gavetas sumiam no que o App
         chamava de "mobile", que era mdAndDown.

         São 24px, e não os 48 de antes: o v-container de cada tela já acrescenta 16px
         por dentro, então o que se vê é a soma dos dois. Com 48 aqui dava 64px de cada
         lado, perto do que as gavetas davam — e largo demais para conteúdo que é quase
         sempre tabela ou grade de cards. -->
    <!-- A folga lateral das telas largas não vale nas públicas: lá a tela é um painel que vai de
         borda a borda, e 24px de branco de cada lado o transformariam numa faixa flutuando. -->
    <v-main :class="$route.meta.public ? 'px-0' : 'px-0 px-lg-6'">
      <router-view v-if="ready"
          :key="`${$route.path}#${seeAllVersion}`"
          @updateSystem="onUpdateSystem($event)"
          @showMessage="showMessage($event)"
          @showErrorMessage="showErrorMessage($event)"
          @showSuccessMessage="showSuccessMessage($event)"
          @crumb="crumb = $event"
          :workspace="workspace"
          class="print-area"
      ></router-view>
      <v-progress-linear v-else indeterminate></v-progress-linear>

      <!-- A pergunta do selo. Aqui fora do router-view porque o selo é da BARRA: ele acompanha
           qualquer tela, e o diálogo tem de sobreviver à que estiver aberta. O mesmo
           componente que o design usa para encerrar, com o sentido invertido. -->
      <ontos-workspace-closure-dialog v-if="askingReopen" :closing="false"
                                      :name="workspace.title"
                                      @confirmed="reopenWorkspace()"
                                      @cancelled="askingReopen = false"/>

      <!-- A janela de criar das portas que criam por cima da tela — o "+ Criar X" da faixa, o "Criar"
           da lista no celular e o "+" da página do ambiente (ver o `requestCreation`); o da barra e o
           das abas levam à página de criar. Aqui fora pelo mesmo motivo do diálogo acima: ela
           sobrevive à tela que estiver aberta, e criar não tira ninguém dela. É a janela da ficha
           aberta já criando: ao salvar, vira a ficha do item. -->
      <ontos-view-instance-dialog v-if="creating && hasWorkspace"
                                  :creating="{known: creating.known}"
                                  :context="{system: system.alias, idea: creating.idea}"
                                  :workspace="workspace"
                                  @created="noteCreated(creating.idea, $event._instance)"
                                  @closed="closeCreation()"
                                  @showMessage="showMessage($event)"
                                  @showErrorMessage="showErrorMessage($event)"/>

      <!-- O design da coleção, aberto pelo "Design" da faixa. Um só, no casco: era uma janela em
           cada tela que tinha o botão. -->
      <ontos-view-class-dialog v-if="designing && hasWorkspace"
                               :idea-alias="designing" :workspace="workspace"
                               @closed="designing = null"
                               @showMessage="showMessage($event)"
                               @showErrorMessage="showErrorMessage($event)"/>

      <!-- A recusa de uma gravação, numa janela (ver o `refuse`). Por último, para abrir por cima da
           janela em que se gravou -->
      <ontos-refusal-dialog/>
      <v-snackbar v-model="hasMessage" :color="messageType" :timeout="messageType == 'error' ? undefined : 1000">
        <div v-html="message"></div>
        <template #actions v-if="messageType == 'error'">
          <v-btn variant="text" @click="hasMessage = false">{{ $tt('close') }}</v-btn>
        </template>
      </v-snackbar>
    </v-main>
  </v-app>
</template>

<script>
import {enhanceWorkspace} from "@/types/Workspace";
import AppBreadcrumb from "@/components/shell/AppBreadcrumb.vue";
import CollectionTabs from "@/components/shell/CollectionTabs.vue";
import CollectionViewTabs from "@/components/live/instance/CollectionViewTabs.vue";
import WorkspaceMenu from "@/components/shell/WorkspaceMenu.vue";
import GlobalSearch from "@/components/shell/GlobalSearch.vue";
import NavDestinations from "@/components/shell/NavDestinations.vue";
import WorkspaceClosureDialog from '@/components/shared/WorkspaceClosureDialog.vue'
import UserAvatar from '@/components/shared/UserAvatar.vue'
import {CredentialError} from "@/errors/errors";
import {BAR_COLOR, WORKSPACE_ICON} from "@/utils/destinations";
import {isApprover} from "@/types/Access";
import {levelOption} from "@/types/AccessLevel";
import {seeingAll, setSeeingAll} from "@/utils/seeAll";
import {canCreateIn, createRoute, creationRequest, noteCreated, requestCreation} from "@/utils/instanceCreation";
import {collectionDesignRoute, collectionRoute} from "@/utils/breadcrumb";
import BarMenu from "@/components/shell/BarMenu.vue";
import ColoredValue from "@/components/shared/ColoredValue.vue";
import ViewInstanceDialog from "@/components/live/instance/ViewInstanceDialog.vue";
import ViewClassDialog from "@/components/design/class/ViewClassDialog.vue";
import RefusalDialog from "@/components/shared/RefusalDialog.vue";
import {WORKSPACE, WORKSPACE_PEOPLE} from "@/components/shared/WorkspaceRouteComponent";
import {computed} from "vue";

/**
 * Os desenhos da chave-mestra: o `key` e o `key_off` do Material Symbols Outlined (fonts.google.com/
 * icons), escolhidos pelo usuário. O MDI, que o resto do app usa, não tem a chave desligada. Só os
 * dois caminhos, e não a fonte inteira, que viria por dois ícones.
 */
const MASTER_KEY_ICON = {
  on: 'M223.5-423.5Q200-447 200-480t23.5-56.5Q247-560 280-560t56.5 23.5Q360-513 360-480t-23.5 56.5Q313-400 280-400t-56.5-23.5ZM280-240q-100 0-170-70T40-480q0-100 70-170t170-70q67 0 121.5 33t86.5 87h352l120 120-180 180-80-60-80 60-85-60h-47q-32 54-86.5 87T280-240Zm0-80q56 0 98.5-34t56.5-86h125l58 41 82-61 71 55 75-75-40-40H435q-14-52-56.5-86T280-640q-66 0-113 47t-47 113q0 66 47 113t113 47Z',
  off: 'M790-57 488-359q-32 54-87 86.5T280-240q-100 0-170-70T40-480q0-66 32.5-121t86.5-87L57-790l57-56 732 733-56 56Zm50-543 120 120-183 183-127-126 50-37 72 54 75-74-40-40H553l-80-80h367ZM280-320q51 0 90.5-27.5T428-419l-56-56-48.5-48.5L275-572l-56-56q-44 18-71.5 57.5T120-480q0 66 47 113t113 47Zm-56.5-103.5Q200-447 200-480t23.5-56.5Q247-560 280-560t56.5 23.5Q360-513 360-480t-23.5 56.5Q313-400 280-400t-56.5-23.5Z'
}

/*
 * As telas em que a faixa do caminho oferece os botões de uma coleção — a da rota. Três grupos, e
 * cada botão diz quais quer (ver os getters do `ideaOnStrip`).
 */
/** A lista, o Kanban, a ficha de um item e a edição dele. */
const COLLECTION_ROUTES = ['list-instances', 'kanban', 'view-instance', 'edit-instance']
/** As telas de um item — onde a coleção não está à vista, e o botão dela leva de volta. */
const ITEM_ROUTES = ['view-instance', 'edit-instance', 'create-instance', 'relationship-kanban']
/**
 * O design da coleção e as telas dele — os botões da faixa valem também nelas (pedido do usuário),
 * desde que deixaram de ser outro modo.
 */
const COLLECTION_DESIGN_ROUTES = ['view-idea', 'edit-idea']

export default {
  name: 'App',

  components: {
    'ontos-app-breadcrumb': AppBreadcrumb,
    'ontos-collection-tabs': CollectionTabs,
    'ontos-collection-view-tabs': CollectionViewTabs,
    'ontos-workspace-menu': WorkspaceMenu,
    'ontos-global-search': GlobalSearch,
    'ontos-nav-destinations': NavDestinations,
    'ontos-workspace-closure-dialog': WorkspaceClosureDialog,
    'ontos-user-avatar': UserAvatar,
    'ontos-bar-menu': BarMenu,
    'ontos-colored-value': ColoredValue,
    'ontos-view-instance-dialog': ViewInstanceDialog,
    'ontos-view-class-dialog': ViewClassDialog,
    'ontos-refusal-dialog': RefusalDialog
  },

  /**
   * O ambiente e as pessoas dele, oferecidos a TODO componente abaixo — as telas da rota e as janelas
   * do casco (a de criar da faixa do caminho, a ficha em janela), que não estão dentro de tela nenhuma.
   * Era a base das telas (`WorkspaceRouteComponent`) quem oferecia, e a janela de criar aberta daqui ficava
   * sem eles: o markdown sem as referências, o campo de usuário sem as pessoas (relato do usuário).
   *
   * `computed`: quem recebe acompanha a troca e a releitura do ambiente.
   */
  provide() {
    return {
      [WORKSPACE]: computed(() => this.hasWorkspace ? this.workspace : null),
      [WORKSPACE_PEOPLE]: computed(() => this.hasWorkspace ? this.workspace.people : [])
    }
  },

  data: () => {
    return {
      system: {title: '', alias: ''},
      hasMessage: false,
      message: '',
      messageType: '',
      workspace: {},
      askingReopen: false,
      crumb: null,
      ready: false,
      //Sobe a cada troca da chave-mestra: relê a escolha e remonta a tela — ver `toggleSeeAll`
      seeAllVersion: 0,
      //A coleção cujo design está aberto na janela do casco — o "Design" da faixa
      designing: null,
      barColor: BAR_COLOR,
      MASTER_KEY_ICON,
      WORKSPACE_ICON
    }
  },

  async created() {
    try {
         await this.reloadSystem(this.$route)
    } catch (err) {
      if (err instanceof CredentialError) await this.$router.push({name: 'login'})
      else throw err
    }
  },

  watch: {
    /**
     * A janela de design é por cima da tela; mudando a tela, ela fecha. O Expandir da propriedade, de dentro
     * dela, leva à página da propriedade — e a janela ficava por cima da página (o Expandir do próprio
     * design já fechava, pelo `closed` de quem expande).
     */
    '$route.path'() {
      this.designing = null
    },

    '$route.params.system': {
      /**
       * Recarrega quando o parâmetro MUDA — inclusive quando ele some.
       *
       * Antes a condição exigia `newSystem` verdadeiro, então sair de um ambiente para a
       * home não fazia nada e `this.system` continuava apontando para o ambiente
       * anterior. Com o estado mentindo, a barra se dava por dentro de um ambiente e
       * montava links com `system: undefined`; no vue-router 4+ isso deixou de ser um
       * aviso e passou a ser exceção ("Missing required param"). É o mesmo motivo de o
       * `hasWorkspace` exigir o alias, e não só o objeto.
       *
       * O reloadSystem já sabia tratar o caso vazio — só nunca era chamado nele.
       */
      async handler(newSystem) {
        const oldSystem = this.system && this.system.alias || null
        if ((newSystem || null) !== oldSystem) {
          //As janelas de criar e de design são do ambiente que se deixou: as coleções dele não são as
          //de agora
          this.closeCreation()
          this.designing = null
          await this.reloadSystem(this.$route)
        }
      }
    }
  },

  computed: {
    /**
     * O nome do item aberto, publicado pela tela pelo evento `crumb`.
     *
     * Compara a IDENTIDADE em vez de limpar num watch de rota. O `:key="$route.path"`
     * destrói a tela antiga no mesmo tique, mas o `load()` dela já está em voo e o emit
     * chega depois — e emit de componente desmontado ainda chama o handler do pai. Um
     * watch que zerasse o rótulo seria sobrescrito por esse emit atrasado, e o caminho
     * mostraria o nome do item ANTERIOR no item atual. Assim ele se invalida sozinho:
     * não há o que limpar, e não há corrida.
     */
    instanceLabel() {
      const crumb = this.crumb
      if (!crumb) return null
      const params = this.$route.params
      const sameItem = crumb.system === params.system
          && crumb.idea === params.idea
          && String(crumb.instance) === String(params.instance)
      return sameItem ? crumb.text : null
    },

    /**
     * A coleção cujas visões — lista e Kanban — aparecem na faixa do caminho.
     *
     * Null fora das duas telas de visão, porque alternar entre elas só faz sentido
     * estando numa delas; e null no xs, onde a faixa não existe e as abas voltam para o
     * cabeçalho da página.
     */
    viewTabsIdea() {
      return this.ideaOnStrip(['list-instances', 'kanban'])
    },

    /**
     * A coleção da tela, para o botão que volta à lista ou ao Kanban dela: nas telas de um item (no
     * Kanban de um relacionamento, a coleção DONA) e nas do design dela (pedido do usuário). Não na
     * lista nem no Kanban da coleção: lá a faixa já mostra a coleção, nas abas Lista/Kanban, e o botão
     * levaria aonde já se está.
     */
    itemIdea() {
      return this.ideaOnStrip([...ITEM_ROUTES, ...COLLECTION_DESIGN_ROUTES])
    },

    /** O destino da aba da coleção na barra — o mesmo `collectionRoute`, lista ou Kanban. */
    itemCollectionRoute() {
      return this.itemIdea && collectionRoute(this.itemIdea, this.system.alias)
    },

    /**
     * A coleção em que o "Criar" da faixa cria: a da lista, a do Kanban, a da ficha de um item e a da
     * edição dele, e as do design dela. Na ficha e na edição ele cria OUTRO item da mesma coleção
     * (decisão do usuário), e não um relacionado — e na edição a janela abre por cima, sem perder o
     * que se digitou.
     */
    createIdea() {
      const idea = this.ideaOnStrip([...COLLECTION_ROUTES, ...COLLECTION_DESIGN_ROUTES])
      return idea && canCreateIn(this.workspace, idea) ? idea : null
    },

    /**
     * A coleção que o "Configurar" da faixa abre: a mesma do "Criar", nas mesmas telas, e também na
     * página de criar (pedido do usuário) — onde o "Criar" não faz sentido, mas configurar a coleção
     * sim. Não no próprio design da coleção, onde abriria por cima a mesma tela que já está aberta.
     * Só para quem desenha (pedido do usuário: quem não tem permissão de design nem vê o botão).
     */
    designIdea() {
      if (!this.workspace.is || !this.workspace.is('designer')) return null
      const designRoutes = COLLECTION_DESIGN_ROUTES.filter(route => route !== 'view-idea')
      return this.ideaOnStrip([...COLLECTION_ROUTES, 'create-instance', ...designRoutes])
    },

    /**
     * O "Configurar" do ambiente na faixa: no início dele, para quem desenha. Fora do xs, como todo
     * botão da faixa, que no xs não existe.
     */
    designsWorkspaceHere() {
      return this.hasWorkspace && !this.$vuetify.display.xs && this.$route.name === 'view-system'
          && this.workspace.is('designer')
    },

    /** A faixa do design do ambiente, onde mora o botão que volta ao início dele. */
    workspaceDesignOnStrip() {
      return this.hasWorkspace && !this.$vuetify.display.xs && this.$route.name === 'design-system'
    },

    /** O menu "+ Criar" da barra: as coleções em que se pode criar, cada uma para a página de criar. */
    createItems() {
      return (this.workspace.ideas || [])
          .filter(idea => canCreateIn(this.workspace, idea))
          .map(idea => ({
            key: idea.alias,
            title: this.$tt('instance-create', {collection: idea.name}),
            to: createRoute(this.system.alias, idea.alias)
          }))
    },

    /**
     * O menu "Configurar" da barra ("Design" em inglês): as coleções e depois o ambiente, cada grupo
     * com o seu título, e cada item com o verbo e a coleção no plural — "Configurar Canções" (forma
     * escolhida pelo usuário). Cada um leva à página de design dele. Só para quem
     * desenha: as duas páginas exigem esse nível.
     *
     * O botão tem chave própria (`design-menu`), e não o `design-verb`: este é o degrau DESIGN do
     * caminho, que continua "Design".
     */
    designItems() {
      if (!this.workspace.is || !this.workspace.is('designer')) return []
      return [
        ...(this.workspace.ideas || []).map(idea => ({
          key: idea.alias, group: this.$tt('collections'),
          title: this.$tt('design-of', {name: idea.pluralName}),
          to: collectionDesignRoute(idea, this.system.alias)
        })),
        {key: '', group: this.$tt('workspace'),
          title: this.$tt('design-of', {name: this.workspace.title}),
          to: {name: 'design-system', params: {system: this.system.alias}}}
      ]
    },

    /**
     * Os menus da barra que, no telefone, vão para dentro do ⋮ — o Configurar, que ali não cabia
     * (pedido do usuário). Vazio sem ambiente ou sem nada a configurar.
     */
    foldedMenus() {
      if (!this.hasWorkspace || !this.$vuetify.display.xs || !this.designItems.length) return []
      return [{key: 'design', icon: 'mdi-tune', label: this.$tt('design-menu'), items: this.designItems}]
    },

    /** O pedido de criação em curso, que a janela do casco atende — ver o `requestCreation`. */
    creating() {
      return creationRequest.value
    },

    /**
     * Há ambiente carregado para mostrar na barra.
     *
     * Exige o `alias`, e não só o objeto: fora de um ambiente o reloadSystem zera o
     * `system`, mas o `workspace` continua sendo o do ambiente anterior — montar um
     * `:to` com `system: undefined` é exceção no vue-router 4, não aviso.
     */
    hasWorkspace() {
      return !!(this.ready && this.system && this.system.alias)
    },

    /** Reabrir é do administrador — e pergunta `is`, porque num encerrado o `can` é falso. */
    canReopen() {
      return this.hasWorkspace && this.workspace.is('admin')
    },

    /**
     * O selo é clicável só para quem reabre, e no xs ele é um ícone.
     *
     * Duas classes porque são duas perguntas diferentes: quem NÃO reabre não recebe ponteiro de
     * mão nem realce no hover, e o recuo menor é de quem mostra um glifo no lugar da palavra.
     */
    badgeClasses() {
      return {
        'closed-badge-static': !this.canReopen,
        'closed-badge--icon': this.$vuetify.display.xs
      }
    },

    /**
     * A chave-mestra aparece? Só para o administrador (decisão do usuário: era também do designer;
     * o servidor confere de novo, no `seesAll`), e só se alguma coleção tem fórmula de acesso: sem
     * nenhuma, não haveria nada a mais para ver.
     */
    offersSeeAll() {
      return this.hasWorkspace && this.workspace.is('admin') && this.workspace.hasAccessRules()
    },

    /** A chave-mestra ligada neste ambiente. Relida a cada troca (`seeAllVersion`): é guardada fora do Vue. */
    seeAll() {
      return this.seeAllVersion >= 0 && this.offersSeeAll && seeingAll(this.system.alias)
    }
  },

  methods: {
    //expostos ao template: função de módulo não é enxergada de lá
    isApprover,
    levelOption,
    requestCreation,
    noteCreated,

    closeCreation() {
      creationRequest.value = null
    },
    /**
     * A coleção da rota, se ela é uma destas telas e a faixa do caminho existe. Null no xs, onde a
     * faixa some e o que ela levaria volta para o cabeçalho da página.
     */
    ideaOnStrip(routes) {
      if (this.$vuetify.display.xs) return null
      if (!routes.includes(this.$route.name)) return null
      const alias = this.$route.params.idea
      return this.workspace && this.workspace.ideaWith && this.workspace.ideaWith(alias) || null
    },

    async  reloadSystem(route) {
      this.ready = false
      let systemAlias = route.params.system;
      if (systemAlias) {
        //Uma ida só: o $lookup de ideias do systems/:alias já resolve o _workflow de cada
        //coleção. Antes vinha daqui e era jogado fora por um $ideas.list logo abaixo, que
        //refazia a mesma consulta só para hidratar o fluxo.
        this.workspace = enhanceWorkspace(await this.$systems.get({systemAlias}, {workflows: true, ideas: true, domains: true}))
        this.system = this.workspace
        if (await this.leaveIfDenied(route)) return
      } else {
        this.system = null
      }
      this.ready = true
    },

    /**
     * O que esta rota exige, e para onde vai quem não tem — nem por endereço digitado.
     *
     * AQUI, e não num `router.beforeEach`: o guarda global roda antes de o ambiente existir e
     * teria de ler o nível e o encerramento do servidor por conta própria, repetindo nesta
     * mesma navegação a requisição que acabou de ser feita acima. Aqui o dado está na mão.
     *
     * E antes de o `ready` virar true, que é o que segura o `router-view`: o desvio acontece
     * sem a tela proibida chegar a aparecer.
     *
     * **Uma pergunta só para as duas recusas**, porque o encerramento já mora dentro do `can`:
     * rota que altera exige nível E ambiente aberto; rota que só lê exige o nível, e por isso
     * o design de um ambiente encerrado continua abrindo. Era um `leaveIfClosed` que só sabia
     * da primeira metade.
     *
     * Quem perde uma rota de ALTERAÇÃO volta para a leitura dela, que o `writes` nomeia. Quem
     * perde uma tela de leitura restrita — um editor no design de uma coleção — volta para a lista
     * da coleção, se a rota tem uma, ou para o início do ambiente. Os params vão inteiros: a rota de
     * destino usa os que precisa e ignora o resto.
     */
    async leaveIfDenied(route) {
      const needs = route.meta.needs
      if (!needs) return false
      const back = route.meta.writes
      if (back ? this.workspace.can(needs) : this.workspace.is(needs)) return false
      const fallback = back || (route.params.idea ? 'list-instances' : 'view-system')
      await this.$router.replace({name: fallback, params: route.params})
      return true
    },

    logout() {
      this.$auth.logout()
      this.$router.push({name: 'login'})
    },

    showMessage(message) {
      this.messageType = 'info'
      this.hasMessage = true
      this.message = message
    },

    showSuccessMessage(message) {
      this.messageType = 'success'
      this.hasMessage = true
      this.message = message
    },

    showErrorMessage(message) {
      this.messageType = 'error'
      this.hasMessage = true
      this.message = message
    },

    async onUpdateSystem() {
      await this.reloadSystem(this.$route)
    },

    /**
     * Liga ou desliga a chave-mestra — e relê o ambiente e a tela: tudo o que está desenhado foi lido
     * com o acesso de antes.
     */
    async toggleSeeAll() {
      setSeeingAll(this.system.alias, !this.seeAll)
      this.seeAllVersion++
      await this.onUpdateSystem()
    },

    /**
     * Reabre pelo selo da barra, depois de confirmado.
     *
     * O `setClosed` do serviço é quem sabe o corpo exato que o servidor aceita — num ambiente
     * encerrado a única escrita que passa é um PATCH com `{closed: false}` e mais nada. Daqui
     * sai só o que é desta tela: reler o ambiente, que é o que apaga o selo e devolve os
     * botões em todas as outras.
     */
    async reopenWorkspace() {
      this.askingReopen = false
      try {
        await this.$systems.setClosed({systemAlias: this.workspace.alias}, false)
        await this.onUpdateSystem()
      } catch (err) {
        this.showErrorMessage(err.message)
      }
    }
  }
}
</script>
<style>
/* Rótulo de um campo que NÃO é um input do Vuetify — o markdown, o de mídia e o editor de
   fórmula desenham a própria caixa, e o rótulo fica por conta deles. Estas são as medidas do rótulo flutuado
   dos campos do Vuetify, que é o que o resto do formulário mostra: sem isto o rótulo sai
   com a fonte do corpo, 16px e preto, e destoa dos vizinhos na mesma coluna.

   Global, e não em cada um: eram dois componentes com a mesma regra escrita à mão, e a
   regra do * logo abaixo já vive aqui pelo mesmo motivo. */
.ontos-field-label {
  display: block;
  font-size: 0.75rem;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.6);
}

/* A alça de arrastar uma lista que se reordena (o `handle` do vuedraggable): mão aberta para agarrar,
   fechada enquanto segura — sem estado em JS para isso. E onde o item vai cair (o `ghost-class`): a
   pergunta de quem arrasta é "onde isto vai parar". Com `!important`: o item tem a cor dele (a
   plaquinha da propriedade é branca, numa regra com escopo, mais específica), e enquanto é a marca de
   onde vai cair, a marca é que vale.

   Globais porque eram dois componentes com as mesmas regras escritas à mão (as opções e situações, as
   dimensões do quadro), e as propriedades da coleção seriam o terceiro. */
.ontos-grip {
  cursor: grab;
}

.ontos-grip:active {
  cursor: grabbing;
}

.ontos-drop-ghost {
  background-color: #cce0ff !important;
}
/* A régua de um item de coleção ao lado do nome dele, dentro de um ITEM DE LISTA — a opção de
   uma lista suspensa de relacionamento, a do menu de raias, a do modal de troca rápida.

   A altura é dita aqui porque ali ela não tem de onde vir: a régua é filha única do `prepend`,
   o pai mede pelo filho e o filho pelo pai, e ela some. 20px é a linha do nome, e não a da
   linha inteira — o próprio ColorAccent documenta essa armadilha.

   6px entre a régua e o nome, os mesmos do `.pv-rel`: os dois são UMA coisa — a marca e o que
   ela marca —, e a folga maior os separava. Era 12px, herdado do respiro que o `prepend` de uma
   lista suspensa dá a um ícone; dentro da caixa de seleção do menu de raias isso deixava a
   régua mais perto da caixa do que do nome a que ela pertence.

   Global, e não em cada um: a receita estava escrita à mão nos dois campos de relacionamento,
   com os mesmos valores e quase o mesmo comentário. É o mesmo motivo do `ontos-field-label`
   logo acima. */
.ontos-rel-accent-list {
  --ontos-accent-width: 3px;
  --ontos-accent-height: 20px;
  margin-right: 6px;
}

/* A fonte de CÓDIGO: o que se escreve tal e qual numa fórmula ou numa URL — o identificador (alias),
   o nome em fórmulas, a própria fórmula (pedido do usuário). Monoespaçada, para se ler como nome
   técnico, e não como frase.

   Um dono: a variável, que o editor de fórmula também lê (o tema dele, no FormulaInput), e a
   classe, que serve ao texto solto e ao campo. No campo, só o valor digitado — o rótulo continua
   com a fonte dos vizinhos. */
:root {
  --ontos-code-font: monospace;
}

.ontos-code:not(.v-input),
.v-input.ontos-code input {
  font-family: var(--ontos-code-font);
}

/* O recuo dos valores dos campos — o px-3 do ViewInfo —, para o que fica ao lado deles começar na
   mesma margem: o markdown longo numa seção, a linha do item relacionado e as subabas da ficha, a
   descrição no design do ambiente. Sem ele, cada peça nova começava 12px à esquerda dos campos
   (relato do usuário). */
.ontos-inset {
  padding-left: 12px;
  padding-right: 12px;
}

/* A tabela de itens com a cara da lista da tela inicial (escolha do usuário): a grade da coleção e as
   tabelas de relacionamento da ficha. Sem o cartão com sombra; linhas mais altas, separadas por um
   fio; o nome das colunas pequeno e cinza; o nome do item (`ontos-list-name`) em destaque e os outros
   valores em cinza escuro. Um dono, aqui, porque as duas telas têm de ficar iguais. A altura e as
   cores só nas linhas de item (`ontos-list-row`): a linha de filtros e a célula da lista do telefone
   têm as suas. */
.ontos-list-table {
  box-shadow: none !important;
  background: transparent !important;
}

.ontos-list-table th {
  font-size: 0.75rem !important;
  font-weight: 400 !important;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity)) !important;
  height: 36px !important;
}

.ontos-list-table .ontos-list-row > td {
  height: auto !important;
  padding-top: 12px !important;
  padding-bottom: 12px !important;
  vertical-align: top;
  font-size: 0.875rem;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.ontos-list-table .ontos-list-row > td.ontos-list-name {
  font-size: 1rem;
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
}

/* O que aparece DEPOIS do resto da tela — o editar e o excluir da ficha, que esperam a leitura
   completa; o rodapé de autoria — entra acendendo, em vez de surgir num quadro.

   Animação de CSS, e não `<Transition>`: roda sozinha quando o elemento é inserido, sem que cada
   `v-if` precise de um embrulho. Os botões de cabeçalho a recebem pelo HeaderAction, todos de uma
   vez. Com "reduzir movimento", nada. */
@media (prefers-reduced-motion: no-preference) {
  .ontos-arrives {
    animation: ontos-arrive 200ms cubic-bezier(0.0, 0, 0.2, 1);
  }
}

@keyframes ontos-arrive {
  from {
    opacity: 0;
  }
}

.required label::after {
  content: ' *';
  color: rgb(255, 40, 40);
  font-weight: bold;
}

.unique label::after {
  content: ' 🔑';
  color: rgb(255, 40, 40);
  font-weight: bold;
}

.required.unique label::after {
  content: ' * 🔑';
  color: rgb(255, 40, 40);
  font-weight: bold;
}

.readonly label::after {
  content: ' 🔒';
}

.required.readonly label::after {
  content: ' 🔒';
}

.required.unique.readonly label::after {
  content: ' 🔒';
}

.plain-link {
  text-decoration: none;
}

/* O idioma do casco: RÓTULO pequeno em cima, VALOR embaixo. É o que o caminho e os dois
   seletores da barra usam, e é o que faz a barra e o caminho lerem como uma coisa só —
   "Noel" sozinho não diz se é o ambiente, a coleção ou o usuário.

   Global, e não repetido nos três componentes: as medidas têm de andar juntas, senão a
   compactação por largura abaixo acerta um e esquece os outros.

   Os pontos de corte são os do projeto (plugins/vuetify.ts): 960 é o md, 600 é o sm.
   Abaixo deles o nome é o que cede — encolhe a fonte e o teto de caracteres, porque
   cortar cedo em telas estreitas é pior do que ler um pouco menor. */
.shell-stack {
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
}

/* Caixa alta desfigura nome próprio, e o casco quase só mostra nomes dados pelo
   usuário: o ambiente, as coleções. O v-btn — que é a base tanto da aba quanto do
   seletor — impõe maiúsculas e um espaçamento de letra calculado PARA maiúsculas, que
   em caixa mista deixa o texto frouxo. Os dois voltam ao normal aqui.

   Fora de camada, então vence as regras do Vuetify sem precisar de !important. */
.shell-plain-case {
  letter-spacing: normal;
  text-transform: none;
}

/* Os botões de TEXTO da barra — a busca e o criar —, translúcidos sobre a cor da barra, para não
   competirem com o nome do ambiente e as abas. Eram a mesma receita escrita nos dois.

   `flex: 0 0 auto` porque o vizinho deles são as abas de coleção, que crescem para ocupar a sobra:
   sem isto o botão encolheria até sumir antes delas. */
.shell-trigger {
  flex: 0 0 auto;
  padding-inline: 8px;
  opacity: 0.85;
}

/* Os dois corpos do casco, em token: há quem precise do TIPO de um com o CORPO do outro
   (ver .shell-step), e escritos direto na regra os tamanhos teriam de ser repetidos em cada
   ponto de quebra — três números copiados, que divergem no dia em que um deles muda. */
:root {
  --shell-label-size: 0.625rem;
  --shell-value-size: 0.875rem;
}

.shell-label {
  font-size: var(--shell-label-size);
  letter-spacing: 0.06em;
  opacity: 0.6;
  text-transform: uppercase;
}

.shell-value {
  max-width: 28ch;
  overflow: hidden;
  font-size: var(--shell-value-size);
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* O degrau que é um VERBO — CRIAR, EDITAR —, aplicado JUNTO do shell-label: dele vem tudo o que
   faz a palavra ler como legenda do degrau de cima — caixa alta, letra espaçada, esmaecida,
   peso normal — e daqui vem só o corpo, que é o do valor.

   Miúdo como um rótulo de verdade ele sumia na linha: rótulo tem um valor embaixo que o
   sustenta, e este não tem — é o degrau inteiro. */
.shell-step {
  font-size: var(--shell-value-size);
}

@media (max-width: 959px) {
  :root {
    --shell-label-size: 0.5625rem;
    --shell-value-size: 0.8125rem;
  }

  .shell-value {
    max-width: 18ch;
  }
}

@media (max-width: 599px) {
  :root {
    --shell-label-size: 0.5rem;
    --shell-value-size: 0.75rem;
  }

  .shell-label {
    letter-spacing: 0.04em;
  }

  /* `min(12ch, 100%)` e não `12ch`: aqui o seletor encolhe (ver o `.shell-menu`), e um teto
     fixo em `ch` seria maior que a caixa justamente quando a barra está apertada — o nome
     passaria do controle em vez de cortar com reticências. O menor dos dois é o que corta
     sempre no lugar certo. */
  .shell-value {
    max-width: min(12ch, 100%);
  }
}

/* O selo do encerrado: um véu claro sobre a própria barra, e não uma cor própria.

   Chegou a ser âmbar, o tom de aviso. Era demais: encerrar é uma decisão tomada de propósito,
   não uma falha a comunicar — e o selo acompanha o usuário em TODA tela, então uma cor forte
   grita o dia inteiro e em pouco tempo se para de ver. Translúcido, ele se lê quando se olha
   para ele e desaparece quando não.

   Em branco com alfa, e não num cinza fixo: o véu acompanha a cor da barra, qualquer que seja.

   O ponteiro de mão é o que o diferencia de um rótulo: ele é também a saída — clicar reabre —,
   e um selo que responde ao clique sem parecer clicável só se descobre por acidente. */
.closed-badge {
  background-color: rgba(255, 255, 255, 0.16);
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 500;
  cursor: pointer;
}

.closed-badge:hover {
  background-color: rgba(255, 255, 255, 0.26);
}

/* A chave-mestra na barra colorida: o branco do selo de encerrado — contorno desligada, cheio ligada */
.see-all-chip {
  color: rgba(255, 255, 255, 0.9);
  margin-left: 8px;
}

.see-all-chip.v-chip--variant-flat {
  background-color: rgba(255, 255, 255, 0.16);
}

/* O desenho da chave nas medidas do `v-icon` que estava aqui: 16px, na cor do texto do selo, e o
   recuo do `start` quando há a palavra ao lado (no xs, só a chave). */
.master-key-icon {
  width: 16px;
  height: 16px;
  fill: currentColor;
  flex: none;
}

.master-key-icon--start {
  margin-inline-end: 6px;
}

/* Para quem não reabre, o selo volta a ser só aviso: sem mão e sem reagir ao passar por cima.
   Um selo que parece clicável e não é vale menos que um que nunca prometeu nada. */
.closed-badge-static,
.closed-badge-static:hover {
  background-color: rgba(255, 255, 255, 0.16);
  cursor: default;
}

/* O selo quando é um glifo e não uma palavra.

   O recuo cai porque 10px de cada lado em volta de um ícone de 16px fazem uma pílula larga
   demais para o que ela diz; e o `letter-spacing` da palavra continua valendo DEPOIS do glifo —
   é um espaço fantasma de 1px à direita, que descentraliza o ícone dentro do chip. */
.closed-badge--icon {
  padding-inline: 6px;
  letter-spacing: normal;
}

/* O tamanho é dito aqui porque a tag é h1 e o padrão do navegador são 2em: numa barra
   de 48px isso seria o elemento mais pesado da tela, e em telas estreitas comeria a
   largura do ambiente e das coleções.

   Havia também um contorno cinza de 1px em quatro direções, para o logo sobreviver às
   TRÊS cores que a barra assumia. Com duas cores escuras — índigo e ciano — o teal já
   se segura sozinho, e o contorno só embaçava as letras. */
.app-bar-logo {
  font-size: 1.25rem;
  line-height: 1.1;
}

@media (max-width: 959px) {
  .app-bar-logo {
    font-size: 1.125rem;
  }
}

@media (max-width: 599px) {
  .app-bar-logo {
    font-size: 1rem;
  }
}

.normal-white-space {
  white-space: normal;
}

/* Título de card que quebra linha sem partir palavra. O Vuetify 4 deixou o título numa
   linha só, com reticências; o nome de um ambiente ou de uma coleção no card da home
   precisa aparecer inteiro, como aparecia. */
.v-card-title.text-h5 {
  white-space: normal;
  word-break: keep-all;
}

@media print {
  body * {
    visibility: hidden;
  }

  .print-area, .print-area * {
    visibility: visible;
  }

  .print-area {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
  }
}
</style>
